// src/utils/auth.ts - Updated for separated admin_users and game_hosts tables
import {supabase} from '@/services/supabase';
import {User, UserRole} from '@/types';

// Get current user with role info - now checks both admin_users and game_hosts
export const getCurrentUser = async (): Promise<User | null> => {
    try {
        const {data: {user}, error} = await supabase.auth.getUser();

        if (error || !user) {
            console.error('Error getting user:', error);
            return null;
        }

        // First check if user is an admin
        const {data: adminData, error: adminError} = await supabase
            .from('admin_users')
            .select('role, first_name, last_name, full_name, organization_id, account_id, status, created_at, updated_at')
            .eq('id', user.id)
            .single();

        if (adminData && !adminError) {
            // User is an admin
            return {
                id: user.id,
                email: user.email || '',
                first_name: adminData.first_name || null,
                last_name: adminData.last_name || null,
                full_name: adminData.full_name || '',
                role: adminData.role,
                organization_id: adminData.organization_id || null,
                account_id: adminData.account_id || null,
                status: adminData.status,
                created_at: adminData.created_at,
                updated_at: adminData.updated_at,
            };
        }

        // Check if user is a game host
        const {data: hostData, error: hostError} = await supabase
            .from('game_hosts')
            .select('first_name, last_name, full_name, account_id, status, created_at, updated_at, invitation_status')
            .eq('email', user.email) // Match by email for game hosts
            .eq('invitation_status', 'accepted') // Only accepted hosts
            .single();

        if (hostData && !hostError) {
            // User is a game host
            return {
                id: user.id,
                email: user.email || '',
                first_name: hostData.first_name || null,
                last_name: hostData.last_name || null,
                full_name: hostData.full_name || '',
                role: 'host' as UserRole,
                organization_id: null,
                account_id: hostData.account_id || null,
                status: hostData.status,
                created_at: hostData.created_at,
                updated_at: hostData.updated_at,
            };
        }

        console.error('User not found in admin_users or game_hosts tables');
        return null;
    } catch (error) {
        console.error('Error in getCurrentUser:', error);
        return null;
    }
};

// Role checking utilities - now includes org_admin
export const isRole = (user: User | null, role: UserRole): boolean => {
    return user?.role === role;
};

export const isSuperAdmin = (user: User | null): boolean => {
    return user?.role === 'super_admin';
};

export const isOrgAdmin = (user: User | null): boolean => {
    return user?.role === 'org_admin';
};

export const isHost = (user: User | null): boolean => {
    return user?.role === 'host';
};

export const isAdmin = (user: User | null): boolean => {
    return user?.role === 'super_admin' || user?.role === 'org_admin';
};

// Role hierarchy: super_admin > org_admin > host
export const hasRoleOrHigher = (user: User | null, minRole: UserRole): boolean => {
    if (!user) return false;

    const roleHierarchy: Record<UserRole, number> = {
        'host': 1,
        'org_admin': 2,
        'super_admin': 3,
    };

    return roleHierarchy[user.role] >= roleHierarchy[minRole];
};

// Admin utilities - now work with admin_users table
export const promoteToSuperAdmin = async (email: string): Promise<void> => {
    const {error} = await supabase
        .from('admin_users')
        .update({role: 'super_admin'})
        .eq('email', email);

    if (error) {
        throw new Error(`Failed to promote user: ${error.message}`);
    }
};
